import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form, Field, Formik } from 'formik';

import { MiniAppFocusWrapper } from 'src/components/miniapp-focus-wrapper/miniapp-focus-wrapper';
import { getAddressDetailsFromSearchLocation } from 'src/services/location-management.service';
import { hasNoSpecialCharacters } from 'src/helpers/validation';
import { ArrowButton, ArrowDirection, FloatingInput } from 'src/components';
import useDebounce from 'src/hooks/use-debounce';
import ShowIf from '../show-if/show-if';
import './address-form.scss';

export const AddressForm = (props) => {
  const selectedAddressDebounced = useDebounce(props.selectedAddress, 500);

  const [inputDanger, setInputDanger] = useState({}); // TODO: remove this dead code
  const [addressesAutocomplete, setAddressesAutocomplete] = useState([]);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchAutocomplete = async () => {
      const result = await getAddressDetailsFromSearchLocation(
        props.selectedAddress,
        5,
        abortController,
        () => {
          // the the location autocomplete service is down, show all the address fields
          setShowDetails(true);
        },
      );
      if (result) {
        setAddressesAutocomplete(result);
      }
    };

    if (props.selectedAddress && hasNoSpecialCharacters(props.selectedAddress)) {
      fetchAutocomplete();

      return () => {
        abortController.abort();
      };
    }
  }, [selectedAddressDebounced]);

  const provinces = [
    'Eastern Cape',
    'Free State',
    'Gauteng',
    'KwaZulu-Natal',
    'Limpopo',
    'Mpumalanga',
    'Northern Cape',
    'North West',
    'Western Cape',
  ];

  useEffect(() => {
    props.toggleNext(isFormValid(props.initialValues));
  }, [props.initialValues]);

  return (
    <Formik
      initialValues={props.initialValues}
      validate={validateForm}
      validateOnChange={true}
      validateOnBlur={false}
      enableReinitialize={true}
      validateOnReset={true}
    >
      {({ setValues }) => {
        return (
          <div className="AddressForm" data-testid="address-form">
            <Form>
              <Field
                name="street"
                type="text"
                placeholder="Street address"
                component={MiniAppFocusWrapper}
                innerComponent={FloatingInput}
                items={addressesAutocomplete}
                getItemDisplayValue={(item) => item.addressName}
                onInputValueChange={(newValue) => props.setSelectedAddress(newValue)}
                onItemSelect={(item) => {
                  if (item) {
                    props.dropdownTealiumEvent();
                    const selectedItem = item || {};
                    const newValues = {
                      ...selectedItem.address,
                      street: selectedItem.address?.streetNumber
                        ? `${selectedItem.address?.streetNumber} ${selectedItem.address?.street}`
                        : selectedItem.address?.street,
                      building: !!selectedItem.address?.building
                        ? `${selectedItem.address?.unit || ''} ${
                            selectedItem.address?.building || ''
                          }`
                        : '',
                      suburb: selectedItem.address?.suburb || '',
                      city: selectedItem.address?.city || '',
                      province: selectedItem.address?.province || '',
                      postalCode: selectedItem.address?.postalCode || '',
                    };
                    setShowDetails(isShouldDetailsBeShown(newValues));
                    setValues(newValues);
                    return newValues;
                  }
                }}
                fieldType="autocomplete"
                automationIdPrefix={`${props.automationPageName}`}
                isDark={true}
                styleIconOpen="icon-location-icon"
                subtext="Eg. 12 Ridge Street"
                autoFocus
              />

              <div className="show-all-fields-button-container">
                <ArrowButton
                  text={`${showDetails ? 'Hide' : 'Show'} address details`}
                  arrowDirection={showDetails ? ArrowDirection.Up : ArrowDirection.Down}
                  onClick={() => setShowDetails((currentValue) => !currentValue)}
                  automationId={`${props.automationPageName}_Arrow_Button_Show_Address_Details`}
                  style="content-normal"
                />
              </div>

              <ShowIf condition={showDetails}>
                <Field
                  name="building"
                  type="text"
                  placeholder="Complex / Building (Optional)"
                  subtext="Complex, building or mall name, unit or shop number"
                  component={MiniAppFocusWrapper}
                  innerComponent={FloatingInput}
                  fieldType="standard"
                  className="dark"
                  automationid={`${props.automationPageName}_Field_Suburb`}
                />
                <Field
                  name="suburb"
                  type="text"
                  placeholder="Suburb"
                  component={MiniAppFocusWrapper}
                  innerComponent={FloatingInput}
                  fieldType="standard"
                  className="dark"
                  automationid={`${props.automationPageName}_Field_Suburb`}
                />
                <Field
                  name="city"
                  type="text"
                  placeholder="City / Town"
                  component={MiniAppFocusWrapper}
                  innerComponent={FloatingInput}
                  fieldType="standard"
                  className="dark"
                  automationid={`${props.automationPageName}_Field_City`}
                />
                <Field
                  name="province"
                  type="text"
                  placeholder="Province"
                  items={provinces}
                  getItemDisplayValue={(item) => item}
                  component={MiniAppFocusWrapper}
                  innerComponent={FloatingInput}
                  fieldType="select"
                  isDark={true}
                  automationIdPrefix={`${props.automationPageName}_Field_Province`}
                />
                <Field
                  name="postalCode"
                  type="text"
                  maxLength="4"
                  placeholder="Postal code"
                  component={MiniAppFocusWrapper}
                  innerComponent={FloatingInput}
                  fieldType="standard"
                  className="dark bottom-field"
                  automationid={`${props.automationPageName}_Field_Postal_Code`}
                />
              </ShowIf>

              <div className="mini-spacer" />
            </Form>
          </div>
        );
      }}
    </Formik>
  );

  function isShouldDetailsBeShown(values) {
    const invalidSuburb = !values?.suburb;
    const invalidCity = !values?.city;
    const invalidProvince = !values?.province;
    const invalidPostalCode = !values?.postalCode;
    return invalidSuburb || invalidCity || invalidProvince || invalidPostalCode;
  }

  function isFormValid(values) {
    const invalidStreet = !values?.street;
    const invalidSuburb = !values?.suburb;
    const invalidCity = !values?.city;
    const invalidProvince = !values?.province;
    const invalidPostalCode = !values?.postalCode;
    return !(invalidStreet || invalidSuburb || invalidCity || invalidProvince || invalidPostalCode);
  }

  function isFormValuesChanged(oldValues, newValues) {
    return (
      oldValues.street !== newValues.street ||
      oldValues.building !== newValues.building ||
      oldValues.suburb !== newValues.suburb ||
      oldValues.city !== newValues.city ||
      oldValues.province !== newValues.province ||
      oldValues.postalCode !== newValues.postalCode
    );
  }

  function validateForm(values) {
    const errors = {};
    let newInputDanger = inputDanger;

    const invalidStreet = !values?.street || !hasNoSpecialCharacters(values?.street);
    newInputDanger['street'] = invalidStreet;
    errors.street = invalidStreet ? 'The street address entered is invalid' : null;

    if (!!values?.building && !hasNoSpecialCharacters(values?.building)) {
      errors.building = 'The building entered is invalid';
      newInputDanger['building'] = true;
    }

    const invalidSuburb = !values?.suburb || !hasNoSpecialCharacters(values?.suburb);
    newInputDanger['suburb'] = invalidSuburb;
    errors.suburb = invalidSuburb ? 'The suburb entered is invalid' : null;

    const invalidCity = !values?.city || !hasNoSpecialCharacters(values?.city);
    newInputDanger['city'] = invalidCity;
    errors.city = invalidCity ? 'The city / town entered is invalid' : null;

    const invalidProvince = !values?.province || !values?.province;
    newInputDanger['province'] = invalidProvince;
    errors.province = invalidProvince ? 'The province entered is invalid' : null;

    const invalidPostalCode =
      !values?.postalCode ||
      values?.postalCode.length !== 4 ||
      !/^[0-9]*$/.test(values?.postalCode);
    newInputDanger['postalCode'] = invalidPostalCode;
    errors.postalCode = invalidPostalCode ? 'The postal code entered is invalid' : null;

    setInputDanger(newInputDanger);

    let anyErrors = false;
    Object.keys(errors).forEach((key) => {
      if (errors[key] !== null) {
        anyErrors = true;
      }
    });

    if (!anyErrors && isFormValuesChanged(props.initialValues, values)) {
      props.setValues(values);
    }

    props.toggleNext(!anyErrors);
    return errors;
  }
};

AddressForm.propTypes = {
  initialValues: PropTypes.shape({
    street: PropTypes.string,
    suburb: PropTypes.string,
    city: PropTypes.string,
    province: PropTypes.string,
    postalCode: PropTypes.string,
  }).isRequired,
  automationPageName: PropTypes.string,
  dropdownTealiumEvent: PropTypes.func,
  setValues: PropTypes.func,
  toggleNext: PropTypes.func,
  selectedAddress: PropTypes.string,
  setSelectedAddress: PropTypes.func,
};
